import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { observer } from 'mobx-react';
import { Block, FlexDiv, ProgressWithNumber, SearchInput } from '@awarego-ui/components';
import TableServer from '../../../../components/table/TableServer';
import { useStores } from '../../../../hooks/use-stores';
import { TABLE_DEFAULT_PAGE_SIZE } from '../../../../utils/constants';

function normalizeSubjectThreatAreas(subject) {
    let definition = subject.definition || {};
    if (typeof definition === 'string') {
        try {
            definition = JSON.parse(definition);
        } catch (e) {
            definition = {};
        }
    }
    const threatAreas = definition.threatAreas || definition.threatareas || [];
    if (!Array.isArray(threatAreas) || threatAreas.length === 0) {
        return [{ code: 'uncategorized', name: 'Uncategorized' }];
    }
    return threatAreas.map((area) => {
        if (typeof area === 'string') return { code: area, name: area };
        return {
            code:
                area.threatAreaCode ||
                area.code ||
                area.id ||
                area.threatAreaName ||
                'uncategorized',
            name:
                area.threatAreaName ||
                area.name ||
                area.title ||
                area.threatAreaCode ||
                'Uncategorized',
        };
    });
}

function getPagedRows(rows, query) {
    let result = Array.isArray(rows) ? rows.slice() : [];
    if (query.filter) {
        const term = query.filter.toLowerCase();
        result = result.filter((row) =>
            Object.values(row).some((value) =>
                String(value || '').toLowerCase().includes(term)
            )
        );
    }
    if (query.order) {
        const [field, direction = 'asc'] = query.order.split(' ');
        result = result.sort((a, b) => {
            const compare = String(a[field] || '').localeCompare(
                String(b[field] || '')
            );
            return direction === 'desc' ? -compare : compare;
        });
    }
    const count = result.length;
    const pageSize = query.n || count;
    const page = query.p || 0;
    return {
        rows: result.slice(page * pageSize, page * pageSize + pageSize),
        count,
    };
}

function TrainingThreatAreas({ companyId, training }) {
    const { automationStore } = useStores();
    const [query, setQuery] = useState({
        p: 0,
        n: TABLE_DEFAULT_PAGE_SIZE,
        order: 'name asc',
        filter: '',
    });

    useEffect(() => {
        if (!companyId || !training.id) return;
        automationStore.loadThreatAreas(companyId, training.id, query);
    }, [automationStore, companyId, training.id, query]);

    const onChangePagingParams = useCallback((p, n) => {
        setQuery((current) =>
            current.p === p && current.n === n ? current : { ...current, p, n }
        );
    }, []);

    const onChangeSort = useCallback((sortBy) => {
        if (!sortBy || sortBy.length === 0) return;
        const order = `${sortBy[0].id} ${sortBy[0].desc ? 'desc' : 'asc'}`;
        setQuery((current) =>
            current.order === order ? current : { ...current, order }
        );
    }, []);

    const columns = useMemo(
        () => [
            {
                Header: 'Threat area',
                accessor: 'name',
            },
            {
                Header: 'Scenarios',
                accessor: 'scenarios',
            },
            {
                Header: 'Score',
                accessor: 'score',
                Cell: (x) => (
                    <ProgressWithNumber
                        value={
                            x.row.original.total
                                ? Math.round(
                                      (100 * x.value) / x.row.original.total
                                  )
                                : 0
                        }
                        width={150}
                        height={6}
                    />
                ),
            },
        ],
        []
    );

    const fallbackThreatAreas = useMemo(() => {
        const grouped = {};
        (training?._rawData?.subjects || []).forEach((subject) => {
            const score =
                subject.score ??
                (subject.answered || 0) * 0.5 + (subject.viewed || 0) * 0.5;
            normalizeSubjectThreatAreas(subject).forEach((area) => {
                if (!grouped[area.code]) {
                    grouped[area.code] = {
                        code: area.code,
                        name: area.name,
                        scenarios: 0,
                        viewed: 0,
                        answered: 0,
                        score: 0,
                        total: 0,
                    };
                }
                grouped[area.code].scenarios += 1;
                grouped[area.code].viewed += subject.viewed || 0;
                grouped[area.code].answered += subject.answered || 0;
                grouped[area.code].score += score || 0;
                grouped[area.code].total += subject.total || 0;
            });
        });
        return Object.values(grouped);
    }, [training?._rawData?.subjects]);
    const fallbackResult = useMemo(
        () => getPagedRows(fallbackThreatAreas, query),
        [fallbackThreatAreas, query]
    );
    const threatAreasData =
        automationStore.threatAreas.length > 0
            ? automationStore.threatAreas.slice()
            : fallbackResult.rows;
    const threatAreasTotal =
        automationStore.threatAreasTotal > 0
            ? automationStore.threatAreasTotal
            : fallbackResult.count;

    return (
        <Block gap={24} tablePadding noBorder resetMargin>
            <FlexDiv flexGrow={1} justifyEnd my={10}>
                <SearchInput
                    label=""
                    placeholder="Search"
                    searchValue={query.filter}
                    onChange={(e) => {
                        const filter = e.target.value || '';
                        setQuery((current) =>
                            current.filter === filter && current.p === 0
                                ? current
                                : { ...current, p: 0, filter }
                        );
                    }}
                    onClear={() =>
                        setQuery((current) =>
                            current.filter === '' && current.p === 0
                                ? current
                                : { ...current, p: 0, filter: '' }
                        )
                    }
                />
            </FlexDiv>
            <TableServer
                columns={columns}
                data={threatAreasData}
                rowsTotal={threatAreasTotal}
                pagesTotal={Math.ceil(threatAreasTotal / query.n)}
                initialPageSize={query.n}
                defaultSortBy="name"
                defaultSortDescent={false}
                hidePagination={threatAreasTotal <= TABLE_DEFAULT_PAGE_SIZE}
                onChangePagingParams={onChangePagingParams}
                onChangeSort={onChangeSort}
                loadingData={automationStore.loadingThreatAreas}
                manualInitialPageTrigger={query.filter}
            />
        </Block>
    );
}

export default observer(TrainingThreatAreas);
